import { Download, FileSpreadsheet, RefreshCcw, SquarePlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { ScrollArea } from '../../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

import type { ShortcutRunRecord, ShortcutRunReviewData } from './types';

type ShortcutReviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: ShortcutRunRecord | null;
  reviewData: ShortcutRunReviewData | null;
  onReplaceCurrentSheet: () => void;
  onInsertNewDocument: () => void;
  onDownloadResult: () => void;
  onDownloadArtifact: (filename: string) => void;
};

function PreviewTable({ rows }: { rows: (string | number)[][] }) {
  return (
    <div className="overflow-auto rounded-lg border border-slate-800">
      <table className="min-w-full border-collapse text-sm">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`} className="border-b border-slate-800 last:border-b-0">
              {row.map((cell, colIndex) => (
                <td key={`cell-${rowIndex}-${colIndex}`} className="max-w-60 border-r border-slate-800 px-3 py-2 align-top text-slate-200 last:border-r-0">
                  {String(cell ?? '') || ' '}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ShortcutReviewDialog({
  open,
  onOpenChange,
  run,
  reviewData,
  onReplaceCurrentSheet,
  onInsertNewDocument,
  onDownloadResult,
  onDownloadArtifact,
}: ShortcutReviewDialogProps) {
  const preview = reviewData?.preview;
  const diff = reviewData?.diff;
  const artifacts = run?.artifacts_json || [];
  const canApply = Boolean(preview?.table?.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden border-slate-700 bg-slate-950 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
            Review Shortcut output
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Inspect the generated spreadsheet before replacing anything in MapleSheet.
          </DialogDescription>
        </DialogHeader>

        {run ? (
          <div className="space-y-4 overflow-hidden">
            <Card className="grid gap-3 border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Status</div>
                <div className="mt-1"><Badge className="bg-emerald-500/15 text-emerald-300">{run.status}</Badge></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Output file</div>
                <div className="mt-1 break-all">{reviewData?.file.fileName || run.result_file_name || 'Unavailable'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Artifacts</div>
                <div className="mt-1">{artifacts.length}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
                <div className="mt-1 line-clamp-2">{run.remote_summary || 'No summary returned.'}</div>
              </div>
            </Card>

            <Tabs defaultValue="preview" className="overflow-hidden">
              <TabsList className="grid w-full grid-cols-3 bg-slate-900">
                <TabsTrigger value="preview">Sheet preview</TabsTrigger>
                <TabsTrigger value="changes">Changed cells</TabsTrigger>
                <TabsTrigger value="artifacts">Artifacts</TabsTrigger>
              </TabsList>

              <TabsContent value="preview" className="mt-4 overflow-hidden">
                <ScrollArea className="h-[42vh] rounded-lg border border-slate-800 bg-slate-900/50 p-4">
                  {preview ? (
                    <div className="space-y-3">
                      <div className="text-sm text-slate-300">
                        {preview.sheetName ? `${preview.sheetName} · ` : ''}{preview.rowCount} rows × {preview.colCount} columns
                      </div>
                      <PreviewTable rows={preview.previewRows} />
                      {preview.warning ? <p className="text-xs text-slate-500">{preview.warning}</p> : null}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">This result could not be previewed structurally, but you can still download it or open it as a new MapleSheet document when supported.</p>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="changes" className="mt-4 overflow-hidden">
                <ScrollArea className="h-[42vh] rounded-lg border border-slate-800 bg-slate-900/50 p-4">
                  {diff && diff.changedCells.length > 0 ? (
                    <div className="space-y-3">
                      {diff.changedCells.map((change) => (
                        <div key={change.cellId} className="rounded-lg border border-slate-800 bg-slate-900 p-3 text-sm">
                          <div className="mb-2 font-mono text-emerald-300">{change.cellId}</div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <div className="text-xs uppercase tracking-wide text-slate-500">Before</div>
                              <div className="rounded-md border border-slate-800 bg-slate-950 px-2 py-1 text-slate-300">{change.before || ' '}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wide text-slate-500">After</div>
                              <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-2 py-1 text-emerald-100">{change.after || ' '}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {diff.truncated ? <p className="text-xs text-slate-500">Only the first set of changed cells is shown.</p> : null}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No structured diff is available for this result.</p>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="artifacts" className="mt-4 overflow-hidden">
                <ScrollArea className="h-[42vh] rounded-lg border border-slate-800 bg-slate-900/50 p-4">
                  {artifacts.length > 0 ? (
                    <div className="space-y-3">
                      {artifacts.map((artifact) => (
                        <div key={artifact} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 p-3 text-sm text-slate-300">
                          <span className="break-all">{artifact}</span>
                          <Button type="button" variant="outline" className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800" onClick={() => onDownloadArtifact(artifact)}>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No extra artifacts were returned for this run.</p>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={onDownloadResult}>
              <Download className="mr-2 h-4 w-4" />
              Download result
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={!canApply} variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={onInsertNewDocument}>
              <SquarePlus className="mr-2 h-4 w-4" />

              Insert into new document
            </Button>
            <Button type="button" disabled={!canApply} className="bg-emerald-600 text-white hover:bg-emerald-500" onClick={onReplaceCurrentSheet}>
              <RefreshCcw className="mr-2 h-4 w-4" />

              Replace current sheet
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
